import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash, Search, Download } from "lucide-react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
  UploadService,
} from "@/services/APIs";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import { isHR, hasRole, isPTSAccount } from "../../utils/rolePermissions";
import { exportEmployeesToCsv } from "../../utils/exportEmployeeData";

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const EMPLOYEE_TABLE_API = "pts_emp"; // table name for manage-active-record API
const DESIGNATION_SCHEMA_NAME = "PTS Designation"; // or "designation" or "pts_designation_master"
const DEPARTMENT_SCHEMA_NAME = "PTS Department"; // or "department" or "pts_department_master"

// Employee Avatar Component
const EmployeeAvatar = ({
  entity,
  imageId,
  imageCache,
  onImageFetched,
  getInitials,
}: {
  entity: any;
  imageId: string | null;
  imageCache: Record<string, string>;
  onImageFetched: (id: string, url: string) => void;
  getInitials: (entity: any) => string;
}) => {
  const cachedUrl = imageId ? imageCache[imageId] : null;
  const [imageUrl, setImageUrl] = useState<string | null>(cachedUrl ?? null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Update imageUrl when cache updates
  useEffect(() => {
    if (cachedUrl && cachedUrl !== imageUrl) {
      setImageUrl(cachedUrl);
      setImageError(false);
    }
  }, [cachedUrl, imageUrl]);

  useEffect(() => {
    if (imageId && !imageUrl && !imageError && !isLoading && !cachedUrl) {
      setIsLoading(true);
      UploadService.getFileById({ id: imageId })
        .then(imageData => {
          const url =
            imageData?.file_path_thumbnail ?? imageData?.file_path ?? null;
          if (url) {
            setImageUrl(url);
            onImageFetched(imageId, url);
          }
        })
        .catch(error => {
          console.error("Failed to fetch employee image:", error);
          setImageError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId]);

  const initials = getInitials(entity);

  return (
    <Avatar className="h-10 w-10 border-2 border-gray-200 shadow-sm">
      {imageUrl && !imageError ? (
        <AvatarImage
          src={imageUrl}
          alt={initials}
          className="object-cover"
          onError={() => setImageError(true)}
        />
      ) : null}
      <AvatarFallback className="from-primary to-primary/70 text-primary-foreground bg-gradient-to-br text-sm font-semibold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

const EmployeePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [imageCache, setImageCache] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);

  // Show action column only for HR and PTS_Admin roles
  const showActionColumn =
    isHR() ||
    hasRole("PTS_Admin") ||
    hasRole("PTS_Admin".toLowerCase()) ||
    isPTSAccount();

  // Toggle employee active status via dropdown API
  const updateActiveMutation = useMutation({
    mutationFn: (params: { pk: string; is_active: boolean }) =>
      DropdownService.manageActiveRecord({
        table: EMPLOYEE_TABLE_API,
        pk: params.pk,
        is_active: params.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["moduleData", EMPLOYEE_SCHEMA_NAME],
      });
      notify.success("Employee status updated");
    },
    onError: (error: any) => {
      notify.error(error?.message ?? "Failed to update active status");
    },
  });

  // Fetch dropdown data for Designation and Department
  const { data: designationOptions } = useQuery({
    queryKey: ["designation-dropdown", DESIGNATION_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DESIGNATION_SCHEMA_NAME),
    enabled: !!DESIGNATION_SCHEMA_NAME,
  });

  const { data: departmentOptions } = useQuery({
    queryKey: ["department-dropdown", DEPARTMENT_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(DEPARTMENT_SCHEMA_NAME),
    enabled: !!DEPARTMENT_SCHEMA_NAME,
  });

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  const { data: employeeSchemaInfo } = useQuery({
    queryKey: ["schema", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(EMPLOYEE_SCHEMA_NAME),
  });

  // Helper function to format date from ISO string to DD-MM-YYYY
  const formatDate = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "-";
      }
    },
    []
  );

  // Handler to update image cache
  const handleImageFetched = useCallback((id: string, url: string) => {
    setImageCache(prev => ({ ...prev, [id]: url }));
  }, []);

  // Helper function to get employee initials
  const getEmployeeInitials = useCallback(
    (entity: any) => {
      if (!columnGridData?.grid) {
        return "U";
      }
      const firstNameField = columnGridData.grid.find((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        return (
          fieldName.includes("firstname") ??
          fieldName.includes("first_name") ??
          fieldName.includes("fname")
        );
      });
      const lastNameField = columnGridData.grid.find((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        return (
          fieldName.includes("lastname") ??
          fieldName.includes("last_name") ??
          fieldName.includes("lname")
        );
      });

      const firstName = firstNameField
        ? (entity?.[firstNameField.field_name] ?? "")
        : "";
      const lastName = lastNameField
        ? (entity?.[lastNameField.field_name] ?? "")
        : "";

      const firstInitial = firstName?.[0]?.toUpperCase() ?? "";
      const lastInitial = lastName?.[0]?.toUpperCase() ?? "";
      return `${firstInitial}${lastInitial}`;
    },
    [columnGridData]
  );

  // Generate all columns dynamically and customize Designation and Department
  const columns: ProColumns<any>[] = useMemo(() => {
    // Helper functions inside useMemo to prevent the warning
    const getDesignationNameById = (id: string) => {
      if (!id) {
        return null;
      }
      return designationOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    };

    const getDepartmentNameById = (id: string) => {
      if (!id) {
        return null;
      }
      return departmentOptions?.find(
        (item: { value: string; label: string }) => item.value === id
      );
    };

    const handleDelete = async (id: string | undefined) => {
      if (!id) {
        notify.error("Employee ID is missing");
        return;
      }
      try {
        const response = await DynamicModuleService.deleteItem(
          EMPLOYEE_SCHEMA_NAME,
          String(id)
        );
        if (response) {
          // notify.success("Employee deleted successfully");
          queryClient.invalidateQueries({
            queryKey: ["moduleData", EMPLOYEE_SCHEMA_NAME],
          });
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete employee");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Employee ID is missing");
        return;
      }
      navigate(PTSNavigationRoutes.EDIT_EMPLOYEE(String(id)));
    };

    if (!columnGridData?.grid) {
      return [];
    }

    // Find firstname field index to insert image column before it
    const firstNameFieldIndex = columnGridData.grid.findIndex((field: any) => {
      const fieldName = field.field_name.toLowerCase();
      const fieldTitle = field.field_title?.toLowerCase() ?? "";
      return (
        fieldName.includes("firstname") ??
        fieldName.includes("first_name") ??
        fieldName.includes("fname") ??
        fieldTitle.includes("first name") ??
        fieldTitle.includes("firstname")
      );
    });

    const dataColumns = columnGridData.grid
      .filter((field: any) => field.field_name?.toLowerCase() !== "is_active")
      .map((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title;

        const isDesignationField =
          fieldName.includes("designation") ??
          fieldTitle?.toLowerCase().includes("designation");

        const isDepartmentField =
          fieldName.includes("department") ??
          fieldTitle?.toLowerCase().includes("department");

        // Check if this is a Date of Birth field
        const isDateOfBirthField =
          fieldName.includes("dateofbirth") ??
          fieldName.includes("date_of_birth") ??
          fieldName.includes("dob") ??
          fieldTitle?.toLowerCase().includes("date of birth") ??
          fieldTitle?.toLowerCase().includes("birth date");

        const isGenderField =
          fieldName.includes("gender") ??
          fieldTitle?.toLowerCase().includes("gender");

        const isFirstNameField =
          fieldName.includes("firstname") ??
          fieldName.includes("first_name") ??
          fieldName.includes("fname") ??
          fieldTitle?.toLowerCase().includes("first name") ??
          fieldTitle?.toLowerCase().includes("firstname");

        const baseColumn: ProColumns<any> = {
          title: fieldTitle,
          dataIndex: fieldName,
          key: fieldName,
        };

        if (isFirstNameField) {
          baseColumn.render = (_: any, entity: any) => {
            const value = entity?.[fieldName] ?? entity?.[field.field_name];
            const displayValue =
              value === null || value === undefined || value === ""
                ? "-"
                : String(value);
            const recordId =
              entity?.id ?? entity?.ID ?? entity?.Id ?? entity?._id;

            return (
              <button
                type="button"
                className="hover:text-primary/80 cursor-pointer text-left font-medium text-blue-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!recordId}
                title="Edit Employee"
                onClick={() => handleEdit(recordId)}
              >
                {displayValue}
              </button>
            );
          };
        }

        if (isDesignationField) {
          baseColumn.render = (_: any, entity: any) => {
            const value = entity?.[fieldName];
            if (!value) {
              return "-";
            }
            const designation = getDesignationNameById(String(value));
            return designation?.label ?? value;
          };
        }

        if (isDepartmentField) {
          baseColumn.render = (_: any, entity: any) => {
            const value = entity?.[fieldName];
            if (!value) {
              return "-";
            }
            const department = getDepartmentNameById(String(value));
            return department?.label ?? value;
          };
        }

        // Format Date of Birth to DD-MM-YYYY
        if (isDateOfBirthField) {
          baseColumn.render = (_: any, entity: any) => {
            const value = entity?.[fieldName];
            return formatDate(value);
          };
        }

        // Format Gender - capitalize first letter
        if (isGenderField) {
          baseColumn.render = (_: any, entity: any) => {
            const value = entity?.[fieldName];
            if (!value) {
              return "-";
            }
            const genderString = String(value);
            // Capitalize first letter and keep rest lowercase
            return (
              genderString.charAt(0).toUpperCase() +
              genderString.slice(1).toLowerCase()
            );
          };
        }

        return baseColumn;
      });

    // Create image column
    const imageColumn: ProColumns<any> = {
      title: "Photo",
      dataIndex: "img",
      key: "employee_image",
      width: 100,
      fixed: firstNameFieldIndex >= 0 ? undefined : "left",
      render: (_: any, entity: any) => {
        const imageId = entity?.img ?? entity?.image ?? null;
        return (
          <EmployeeAvatar
            entity={entity}
            imageId={imageId}
            imageCache={imageCache}
            onImageFetched={handleImageFetched}
            getInitials={getEmployeeInitials}
          />
        );
      },
    };

    // Insert image column before firstname if found, otherwise at the beginning
    const columnsWithImage =
      firstNameFieldIndex >= 0
        ? [
            ...dataColumns.slice(0, firstNameFieldIndex),
            imageColumn,
            ...dataColumns.slice(firstNameFieldIndex),
          ]
        : [imageColumn, ...dataColumns];

    const activeColumn: ProColumns<any> = {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      align: "center",
      fixed: showActionColumn ? undefined : "right",
      render: (_: any, record: any) => {
        const recordId =
          record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";
        const pkStr = recordId ? String(recordId) : "";
        const isActive =
          record?.is_active === true ||
          record?.is_active === "true" ||
          record?.Is_Active === true;
        const isUpdating =
          updateActiveMutation.isPending &&
          updateActiveMutation.variables?.pk === pkStr;

        return (
          <Switch
            checked={!!isActive}
            disabled={!pkStr || isUpdating}
            onCheckedChange={checked => {
              if (!pkStr) {
                return;
              }
              updateActiveMutation.mutate({ pk: pkStr, is_active: !!checked });
            }}
          />
        );
      },
    };

    const actionColumn: ProColumns<any> = {
      title: "Actions",
      width: 120,
      valueType: "option",
      key: "actions",
      fixed: "right",
      render: (_: any, record: any) => {
        const recordId = record?.id ?? record?.ID ?? record?.Id ?? record?._id;

        return [
          <CustomButton
            key="edit"
            icon={<Edit2 size={16} />}
            type="text"
            onClick={() => {
              handleEdit(recordId);
            }}
            title="Edit Employee"
          />,
          <Popconfirm
            key="delete"
            title="Are you sure you want to delete this employee?"
            onConfirm={() => {
              handleDelete(recordId);
            }}
            okText="Yes"
            cancelText="No"
          >
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              title="Delete Employee"
            />
          </Popconfirm>,
        ];
      },
    };

    return [
      ...columnsWithImage,
      activeColumn,
      ...(showActionColumn ? [actionColumn] : []),
    ];
  }, [
    columnGridData,
    designationOptions,
    departmentOptions,
    queryClient,
    navigate,
    formatDate,
    imageCache,
    handleImageFetched,
    getEmployeeInitials,
    showActionColumn,
    updateActiveMutation,
  ]);

  const handleAddEmployee = () => {
    navigate(PTSNavigationRoutes.CREATE_EMPLOYEE);
  };

  const handleImportEmployee = () => {
    navigate(PTSNavigationRoutes.IMPORT_EMPLOYEE);
  };

  // Build filter object for firstname and lastname search with ordering
  const searchFilter = useMemo(() => {
    const filter: Record<string, any> = {
      ordering: "firstname",
    };

    if (searchQuery?.trim()) {
      const searchTerm = searchQuery.trim();
      filter.firstname__ilike = `%${searchTerm}%`;
      // lastname__ilike: `%${searchTerm}%`,
    }

    return filter;
  }, [searchQuery]);

  const runEmployeeExport = async (selectedOnly = false) => {
    if (selectedOnly && selectedRowKeys.length === 0) {
      notify.warning("Please select at least one employee to export");
      return;
    }

    setIsExporting(true);
    try {
      const { count } = await exportEmployeesToCsv({
        filters: searchFilter,
        selectedIds: selectedOnly
          ? selectedRowKeys.map(key => String(key))
          : undefined,
        searchQuery: searchQuery.trim() || undefined,
        designationOptions: designationOptions ?? [],
        departmentOptions: departmentOptions ?? [],
        columnGridData,
        schemaInfo: employeeSchemaInfo,
      });

      notify.success(`Exported ${count} employee record(s) with all fields`);
      if (selectedOnly) {
        setSelectedRowKeys([]);
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to export employee data";
      if (message.includes("No employee") || message.includes("No selected")) {
        notify.warning(message);
      } else {
        console.error("Export error:", error);
        notify.error(message);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = () => runEmployeeExport(false);
  const handleExportSelected = () => runEmployeeExport(true);

  const toolBarRender = () => {
    return [
      selectedRowKeys.length > 0 ? (
        <Button
          key="export-selected"
          disabled={isExporting}
          onClick={handleExportSelected}
          className="border-primary text-primary hover:bg-primary flex items-center gap-2 border bg-white hover:text-white"
        >
          <Download className="h-4 w-4" />
          Export ({selectedRowKeys.length})
        </Button>
      ) : null,
      <div key="search" className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by first name"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-64 pl-10"
        />
      </div>,
    ];
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Employees
          </h1>
          <p className="text-sm text-[#627384]">
            Manage employee information and records
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleAddEmployee}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
          >
            <Plus className="h-4 w-4" />
            Add New Employee
          </Button>
          <Button
            onClick={handleImportEmployee}
            className="border-primary hover:bg-primary text-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:text-white"
          >
            <Plus className="h-4 w-4" />
            Add Import
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      {/* Employee Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <ConfigDynamicTable
            tableName={EMPLOYEE_SCHEMA_NAME}
            columns={columns}
            headerTitle="Employee List"
            rowKey="id"
            filter={searchFilter}
            toolBarRender={toolBarRender}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeePage;
